import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {SmartForm} from "ui-kit/SmartForm";
import {PlatformReleaseInfo} from "./components/PlatformReleaseInfo";
import releaseService from "../../../core/services/releaseService";
import AudioService from "../../../core/services/audioService";
import isEmpty from "lodash/isEmpty"
import { useParams } from "react-router";
import { ReleasesData } from "./types";
import type { Release } from "./types";
import { ProgressSpinner } from "primereact/progressspinner";

export const FormContent: React.FC = () => {
    const { id } = useParams()
    const {getReleasesData} = releaseService();
    const {getAudioData} = AudioService()
    const [audioMap, setAudioMap] = useState<Record<string, string>>({});
    const [releases, setReleases] = useState<ReleasesData>({
        releases: { data: {} as Release },
    });
    // Fullscreen loader ONLY for the initial release metadata load (NOT for audio).
    // This prevents the "empty Review content for 1-2s" flash.
    const [releaseLoading, setReleaseLoading] = useState<boolean>(true);
    const audioUrlsRef = useRef<Record<string, string>>({});
    const isUnmountedRef = useRef(false);
    const releaseFetchStartedRef = useRef(false);

    const trackIds = useMemo(() => {
      const tracks = releases?.releases?.data?.tracks || [];
      return Array.isArray(tracks) ? tracks.map((t: any) => String(t?.id)).filter(Boolean) : [];
    }, [releases?.releases?.data?.tracks]);
    
    const audio = useMemo(() => {
      return Object.entries(audioMap).map(([id, audio]) => ({ id, audio }));
    }, [audioMap]);
    

    // Lazy audio load: render track info immediately, then fetch audio per-track in background.
    // IMPORTANT: do NOT block the whole Track information accordion with a global spinner.
useEffect(() => {
      if (!trackIds.length) return;

      // Revoke URLs for tracks that are no longer present (safety)
      const currentSet = new Set(trackIds);
      Object.keys(audioUrlsRef.current).forEach((tid) => {
        if (!currentSet.has(tid)) {
          try { URL.revokeObjectURL(audioUrlsRef.current[tid]); } catch { /* noop */ }
          delete audioUrlsRef.current[tid];
        }
      });

      // Fetch missing audio with a small concurrency limit (prevents network spikes).
      let cancelled = false;
      const CONCURRENCY = 2;
      const queue = trackIds.filter((tid) => !audioUrlsRef.current[tid]);

      const fetchOne = async (tid: string) => {
        try {
          const res = await getAudioData(`tracks/${tid}/stream`);
          const audioBlob = new Blob([res.data], { type: "audio/wav" });
          const url = URL.createObjectURL(audioBlob);
          if (cancelled || isUnmountedRef.current) {
            try { URL.revokeObjectURL(url); } catch { /* noop */ }
            return;
          }
          audioUrlsRef.current[tid] = url;
          setAudioMap((prev) => ({ ...prev, [tid]: url }));
        } catch (error) {
          console.error(`Failed to load audio for track ${tid}`, error);
        }
      };

      const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, async () => {
        while (queue.length && !cancelled && !isUnmountedRef.current) {
          const tid = queue.shift();
          if (!tid) break;
          await fetchOne(tid);
        }
      });

      Promise.all(workers).catch(() => { /* ignore */ });

      return () => {
        cancelled = true;
      };
    }, [trackIds, getAudioData]);

    useEffect(() => {
      return () => {
        isUnmountedRef.current = true;
        // Revoke any created object URLs
        Object.values(audioUrlsRef.current).forEach((url) => {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
        });
        audioUrlsRef.current = {};
      };
    }, []);

    useEffect(() => {
        if (!id) {
          setReleaseLoading(false);
          return;
        }
        // React 18 StrictMode can run effects twice in dev; guard to avoid duplicate fetches.
        if (releaseFetchStartedRef.current) return;
        releaseFetchStartedRef.current = true;

        setReleaseLoading(true);
        getReleasesData(`releases/${id}`)
            .then((res) => {
                if (isUnmountedRef.current) return;
                setReleases((prev) => ({
                    ...prev,
                    releases: res.data || {},
                }));
            })
            .catch((err) => {
                console.error("Error fetching user data:", err);
            })
            .finally(() => {
              if (isUnmountedRef.current) return;
              setReleaseLoading(false);
            });
    }, [id, getReleasesData]);

    return (
        <SmartForm<ReleasesData>
            releases={releases}
            onSubmit={(values) => {
                // sendReleases('releases/7/main-genre')

            }}>
            {({values, form}) => {
                return (
                    <Container>
                        {releaseLoading && (
                          <BlockingOverlay>
                            <ProgressSpinner />
                          </BlockingOverlay>
                        )}
                        <PlatformReleaseInfo
                            releases={releases}
                            audio={audio}
                            loading={false}
                            />

                    </Container>
                );
            }}
        </SmartForm>
    );
};

const Container = styled.div`
    background-color: ${(props) => props.theme.palette.background.base};
    width: 100%;
    height: auto;
    position: relative;
`;

const BlockingOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;
